<template>
    <DefaultHeader :noMargin="true" />
    <div :class="{ 'd-none': this.$store.state.hiddenBody }" class="container position-relative">
        <div class="view-switch-container">
            <div class="view-switch btn-group" role="group" aria-label="Mode d'affichage">
                <button type="button" class="btn btn-outline-secondary" :class="{ active: viewMode === 'list' }"
                    @click="viewMode = 'list'">
                    <font-awesome-icon :icon="['fas', 'list']" />
                </button>
                <button type="button" class="btn btn-outline-secondary" :class="{ active: viewMode === 'cards' }"
                    @click="viewMode = 'cards'">
                    <font-awesome-icon :icon="['fas', 'grip']" />
                </button>
            </div>
        </div>
        <!-- Barre de filtres -->
        <div class="row d-flex justify-content-center my-2 g-2">
            <div class="col-10 col-md-3 col-lg-2">
                <SearchDisplay :searchFunction="'searchWord'" @searchWord="onSearch" />
            </div>
            <div v-if="selectedClientId" class="col-10 col-md-3 col-lg-2">
                <select class="form-select" v-model="selectedGroupId" aria-label="Filtrer par groupe"
                    :disabled="!selectedClientId">
                    <option value="">Tous les groupes</option>
                    <option v-for="g in groups" :key="g.id" :value="String(g.id)">{{ g.name }}</option>
                </select>
            </div>
        </div>

        <div v-if="selectedGroup" class="row d-flex justify-content-center mb-3">
            <div class="col-10 col-lg-8">
                <div
                    class="active-group-banner d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                    <div class="mb-2 mb-md-0">
                        <div class="small text-uppercase fw-bold opacity-75">
                            Filtre groupe actif
                        </div>
                        <div class="fw-bold fs-5">
                            {{ selectedGroup.name }}
                        </div>
                        <div class="small mt-1">
                            {{ filteredBalises.length }} véhicule(s) affiché(s)
                        </div>
                    </div>
                    <div>
                        <button class="btn btn-light btn-sm fw-semibold" @click="clearGroupFilter">
                            Afficher tous les véhicules
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!selectedClientId" class="row d-flex justify-content-center mb-3">
            <div class="col-10 col-lg-8">
                <div class="alert alert-light text-center small mb-0">
                    Aucun client sélectionné — sélectionnez un client pour activer le filtrage par groupe et la
                    réorganisation des véhicules.
                </div>
            </div>
        </div>

        <!-- Placeholder quand filtres actifs et aucun résultat -->
        <div v-if="!isLoading && filteredBalises.length === 0" class="row d-flex justify-content-center mb-4 mt-5">
            <div class="col-10 col-md-6 text-center text-muted">
                Aucun véhicule ne correspond à vos filtres.
            </div>
        </div>
        <div v-if="canReorder && filteredBalises.length > 1" class="row d-flex justify-content-center mb-2">
            <div class="col-10 col-md-8 text-center text-muted small">
                Glissez-déposez les véhicules pour modifier leur ordre d'affichage.
            </div>
        </div>
        <div v-else-if="selectedClientId && searchActive && filteredBalises.length > 1"
            class="row d-flex justify-content-center mb-2">
            <div class="col-10 col-md-8 text-center text-muted small">
                Le réordonnancement est désactivé pendant l'utilisation de la recherche.
            </div>
        </div>

        <!-- Liste véhicules -->
        <BalisesListRow v-if="viewMode === 'list'" :balises="filteredBalises" :groups="groups" :canReorder="canReorder"
            :isSavingOrder="isSavingOrder" :draggedBaliseId="draggedBaliseId" :dropTargetBaliseId="dropTargetBaliseId"
            @save="updateBalise" @changeIcon="baliseToChangeIcon" @dragstart="onDragStart" @dragover="onDragOver"
            @dragleave="onDragLeave" @drop="onDrop" @dragend="onDragEnd" />

        <BalisesListCard v-else :balises="filteredBalises" />
    </div>
    <DefaultFooter class="mt-4" />

    <IconPickerModal :baliseToUpdate="baliseToUpdate" />

</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue'
import DefaultFooter from '@/components/DefaultFooter.vue'
import BalisesListRow from './sections/BalisesListRow.vue'
import BalisesListCard from './sections/BalisesListCard.vue'
import IconPickerModal from '@/components/balisesPage/IconPickerModal.vue'
import BaliseService from '@/services/balise.service.js'
import FormatHelper from '@/helpers/format.helper.js'
import SearchDisplay from '@/components/display/SearchDisplay.vue'
import GroupService from '@/services/group.service.js'
import UserHelper from '@/helpers/user.helper.js'
import { readUserPageFilters, writeUserPageFilters } from '@/helpers/localUserFilterStorage.js'

const PAGE_FILTER_KEY = 'balises-list';

export default {
    components: {
        DefaultHeader,
        DefaultFooter,
        IconPickerModal,
        SearchDisplay,
        BalisesListRow,
        BalisesListCard
    },
    data() {
        return {
            balises: null,
            groups: [],         // groupes du client sélectionné
            baliseToUpdate: null,
            searchInput: null,
            selectedGroupId: '',
            viewMode: 'list',
            isLoading: true,
            draggedBaliseId: null,
            dropTargetBaliseId: null,
            isSavingOrder: false
        };
    },
    computed: {
        currentUserId() {
            return this.$store?.state?.auth?.user?.id ?? null;
        },
        pageFilterKey() {
            const clientPart = this.selectedClientId ? String(this.selectedClientId) : 'no-client';
            return `${PAGE_FILTER_KEY}:${clientPart}`;
        },
        filteredBalises() {
            const list = Array.isArray(this.balises) ? this.balises : [];
            if (!this.selectedGroupId) return list;
            const sel = String(this.selectedGroupId);
            return list.filter(b => String(b.groupId ?? '') === sel);
        },
        selectedGroup() {
            if (!this.selectedGroupId) return null;
            return this.groups.find(g => String(g.id) === String(this.selectedGroupId)) || null;
        },
        selectedClientId() {
            return UserHelper.getClientId();
        },
        isAdminOrOperator() {
            return UserHelper.isAdmin() || UserHelper.isOperator();
        },
        filtersActive() {
            return !!(this.selectedGroupId || (this.searchInput && String(this.searchInput).trim()));
        },
        searchActive() {
            return !!(this.searchInput && String(this.searchInput).trim());
        },
        canReorder() {
            return !!this.selectedClientId && !this.searchActive;
        },
    },
    watch: {
        $route() {
            this._onClientChange(true);
        },
        selectedClientId() {
            this.restoreFilters();
            this._onClientChange(true);
        },
        selectedGroupId() {
            this.persistFilters();
            this.draggedBaliseId = null;
            this.dropTargetBaliseId = null;
        }
    },
    mounted() {
        // Initialiser (charge groupes du client courant + balises)
        this.restoreFilters();
        this._onClientChange(true);
    },
    methods: {
        restoreFilters() {
            const saved = readUserPageFilters(this.currentUserId, this.pageFilterKey);
            this.selectedGroupId = saved.selectedGroupId ? String(saved.selectedGroupId) : '';
        },
        persistFilters() {
            writeUserPageFilters(this.currentUserId, this.pageFilterKey, {
                selectedGroupId: this.selectedGroupId || ''
            });
        },
        clearGroupFilter() {
            this.selectedGroupId = '';
        },
        // (ré)agit au changement de client lu via UserHelper
        _onClientChange(reloadData = false) {
            if (this.isAdminOrOperator) {
                if (this.selectedClientId) {
                    this.loadGroupsForClient(this.selectedClientId)
                        .finally(() => {
                            this.ensureSelectedGroupStillExists();
                            if (reloadData) this.refreshData();
                        });
                } else {
                    this.groups = [];
                    this.selectedGroupId = '';
                    if (reloadData) this.refreshData();
                }
            } else {
                this.loadClientGroups()
                    .finally(() => {
                        this.ensureSelectedGroupStillExists();
                        if (reloadData) this.refreshData();
                    });
            }
        },
        onSearch(term) {
            this.searchInput = term;
            this.draggedBaliseId = null;
            this.dropTargetBaliseId = null;
            this.refreshData();
        },
        ensureSelectedGroupStillExists() {
            if (!this.selectedGroupId) return;
            const exists = this.groups.some(g => String(g.id) === String(this.selectedGroupId));
            if (!exists) {
                this.selectedGroupId = '';
            }
        },
        loadClientGroups() {
            return GroupService.getClientGroups()
                .then(response => {
                    const arr = Array.isArray(response.data) ? response.data : [];
                    this.groups = arr
                        .map(g => ({ id: g.id, name: g.name }))
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                })
                .catch(e => console.log(e));
        },
        loadGroupsForClient(clientId) {
            return GroupService.getGroupsByClient(clientId)
                .then(res => {
                    const arr = Array.isArray(res.data) ? res.data : [];
                    this.groups = arr
                        .map(g => ({ id: g.id, name: g.name }))
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                })
                .catch(e => console.log(e));
        },
        refreshData() {
            this.isLoading = true;
            const request = this.isAdminOrOperator
                ? BaliseService.getAllBalises({
                    search: this.searchInput,
                    clientId: this.selectedClientId ? Number(this.selectedClientId) : undefined,
                    groupId: undefined
                })
                : BaliseService.getGroupBalisesLastPositions(this.searchInput);

            request
                .then(response => {
                    this.balises = FormatHelper.balisesResponseFormat(response.data);
                    this.draggedBaliseId = null;
                    this.dropTargetBaliseId = null;
                })
                .catch(e => {
                    console.log(e);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        updateBalise({ bId, name, groupId }) {
            BaliseService.updateData(bId, { name, groupId })
                .then(() => this.refreshData())
                .catch(e => console.log(e));
        },
        baliseToChangeIcon(bId) {
            this.baliseToUpdate = bId;
        },
        onDragStart(event, baliseId) {
            if (!this.canReorder || this.isSavingOrder) {
                event.preventDefault();
                return;
            }

            this.draggedBaliseId = baliseId;
            this.dropTargetBaliseId = null;

            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(baliseId));
            }
        },
        onDragOver(event, baliseId) {
            if (!this.canReorder || this.isSavingOrder || !this.draggedBaliseId) return;

            event.preventDefault();

            if (this.draggedBaliseId !== baliseId) {
                this.dropTargetBaliseId = baliseId;
            } else {
                this.dropTargetBaliseId = null;
            }
        },
        onDragLeave(event, baliseId) {
            if (this.dropTargetBaliseId === baliseId) {
                this.dropTargetBaliseId = null;
            }
        },
        onDrop(event, targetBaliseId) {
            if (!this.canReorder || this.isSavingOrder) return;

            event.preventDefault();

            const sourceBaliseId = this.draggedBaliseId;
            this.dropTargetBaliseId = null;

            if (!sourceBaliseId || sourceBaliseId === targetBaliseId || !Array.isArray(this.balises)) {
                this.draggedBaliseId = null;
                return;
            }

            const fromIndex = this.balises.findIndex(b => b.id === sourceBaliseId);
            const toIndex = this.balises.findIndex(b => b.id === targetBaliseId);

            if (fromIndex === -1 || toIndex === -1) {
                this.draggedBaliseId = null;
                return;
            }

            const previousBalises = [...this.balises];
            const updatedBalises = [...this.balises];
            const [movedBalise] = updatedBalises.splice(fromIndex, 1);
            updatedBalises.splice(toIndex, 0, movedBalise);

            this.balises = updatedBalises;
            this.draggedBaliseId = null;
            this.isSavingOrder = true;

            BaliseService.updateOrder(this.selectedClientId, updatedBalises.map(b => b.id))
                .catch(e => {
                    console.log(e);
                    this.balises = previousBalises;
                })
                .finally(() => {
                    this.isSavingOrder = false;
                });
        },
        onDragEnd() {
            this.draggedBaliseId = null;
            this.dropTargetBaliseId = null;
        }
    }
}
</script>

<style scoped>
.view-switch-container {
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    z-index: 10;
}

.view-switch .btn {
    min-width: 42px;
}

.view-switch .btn.active {
    pointer-events: none;
}

.active-group-banner {
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.14), rgba(13, 110, 253, 0.08));
    border: 2px solid rgba(13, 110, 253, 0.35);
    border-radius: 14px;
    padding: 1rem 1.25rem;
    box-shadow: 0 6px 18px rgba(13, 110, 253, 0.08);
}

@media (max-width: 767.98px) {
    .view-switch-container {
        position: static;
        display: flex;
        justify-content: flex-end;
        margin-top: 0.5rem;
        margin-bottom: 0.75rem;
    }

    .view-switch {
        position: relative;
    }

    .row.g-2>.col-10 {
        width: 100%;
        max-width: 26rem;
    }
}
</style>